/* eslint-disable import/no-anonymous-default-export */
import dayjs from "dayjs";
import i18n from "./Locale/i18n";

type DateFormat = "DD MMM. YYYY" | "DD MMM YYYY" | "DD MMMM YYYY" | "DD MMM HH:mm" | "DD.MM.YYYY" | "DD.MM.YYYY HH:mm" | "YYYY-MM-DD" | "HH:mm" | "DD.MM.YYYY - HH:mm" | "DD.MM" | "dd";

class Utils {
  format(e?: Date | string, format?: DateFormat) {
    if (!e) {
      return " - ";
    }
    return dayjs(e)
      .locale(i18n.language)
      .format(format ? format : "DD MMM. YYYY");
  }

  getEnumLabel(data: any[], value?: number): string {
    try {
      return data.find((e) => e.value === value)?.title;
    } catch (e) {
      return "";
    }
  }

  array(count: number) {
    return Array.from(Array(count).keys());
  }

  generateRandomHexColors(n: number): string[] {
    function getRandomHexColor(): string {
      return "#" + Math.floor(Math.random() * 16777215).toString(16);
    }

    const colors: string[] = [];
    for (let i = 0; i < n; i++) {
      colors.push(getRandomHexColor());
    }
    return colors;
  }

  generateRandomColor() {
    return "#" + (((1 << 24) * Math.random()) | 0).toString(16);
  }

  formatPrice(number?: number): string {
  if (number === null || number === undefined) {
    return "-";
  }

  const formatted = Number(number)
    .toFixed(2)
    .replace(/\.?0+$/, "");

  return formatted.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

  formatPercent(percent: number): string {
    return `${Math.round(percent)}%`;
  }

  /** A project document has a contour once its polygon holds at least one point. */
  hasContour(polygon: unknown): boolean {
    if (!polygon) return false;
    try {
      const parsed: any = typeof polygon === "string" ? JSON.parse(polygon) : polygon;
      return Array.isArray(parsed?.points) && parsed.points.length > 0;
    } catch {
      return false;
    }
  }

  getDaysSinceDate(selectedDate?: Date) {
    if (!selectedDate) {
      return 0;
    }
    let diff = dayjs().diff(dayjs(selectedDate), "day");
    if (diff === 0) {
      diff = 1;
    }
    return diff;
  }

  delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  compressImage = (file: File, maxWidth: number, maxHeight: number, qualityStep: number = 0.1): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        let canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        const compress = (quality: number) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Failed to compress image"));
                return;
              }

              if (blob.size > 100 * 1024 && quality > 0.1) {
                compress(quality - qualityStep);
              } else {
                resolve(blob);
              }
            },
            "image/jpeg",
            quality
          );
        };

        compress(0.9); // Start compression with high quality
      };
      img.onerror = (error) => {
        reject(error);
      };
    });
  };

  trimCanvas(canvas: HTMLCanvasElement): HTMLCanvasElement {
    const ctx = canvas.getContext("2d");
    if (!ctx) return canvas;
    const { width, height } = canvas;
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    let top = null as number | null;
    let bottom = null as number | null;
    let left = null as number | null;
    let right = null as number | null;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4 + 3;
        if (data[idx] > 0) {
          if (top === null) top = y;
          bottom = y;
          if (left === null || x < left) left = x;
          if (right === null || x > right) right = x;
        }
      }
    }

    if (top === null || left === null || bottom === null || right === null) {
      // blank canvas
      return canvas;
    }

    const trimmedWidth = right - left + 1;
    const trimmedHeight = bottom - top + 1;
    const trimmed = document.createElement("canvas");
    trimmed.width = trimmedWidth;
    trimmed.height = trimmedHeight;
    const trimmedCtx = trimmed.getContext("2d");
    if (trimmedCtx) {
      trimmedCtx.putImageData(ctx.getImageData(left, top, trimmedWidth, trimmedHeight), 0, 0);
    }
    return trimmed;
  }
}

export default new Utils();
